import type { FC, ComponentType } from 'react';
import React, { useContext, useCallback, useRef } from 'react';
import { styled } from '@compiled/react';

import { token } from '@atlaskit/tokens';

import { expValEquals } from '@confluence/feature-experiments';
import { LivePagePreloader } from '@confluence/live-page-preloader';

import { PageCardContext } from '../PageCardContext';

export type CardProps = {
	isMenuOpen: boolean;
	customStyles?: object;
	isFolderHoverCard?: boolean;
	isEndOfPageHoverCard?: boolean;
};
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const Card = styled.div<CardProps>(
	{
		position: 'relative',
		backgroundColor: token('elevation.surface'),
		border: `1px solid ${token('color.border')}`,
		borderRadius: token('border.radius.200'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		'--show-action': ({ isMenuOpen }) => (isMenuOpen ? 1 : 0),
	},
	// folder hover cards are not interactive
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	({ isFolderHoverCard }) =>
		!isFolderHoverCard && {
			'&:hover,&:active,&:focus-within': {
				cursor: 'pointer',
				'--show-action': 1,
				boxShadow: token('elevation.shadow.raised'),
			},
		},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	({ isEndOfPageHoverCard }) =>
		isEndOfPageHoverCard && {
			'&:hover': {
				backgroundColor: token('color.background.neutral'),
			},
		},
);

type PageCardContainerProps = {
	StyledCard?: ComponentType<CardProps>;
	children?: React.ReactNode;
	isHoverPageCard?: boolean;
	isTriggerCard?: boolean;
};

export const PageCardContainer: FC<PageCardContainerProps> = ({
	StyledCard = Card,
	children,
	isHoverPageCard,
	isTriggerCard,
}) => {
	const { isMenuOpen, setIsViewed, type, subType, status } = useContext(PageCardContext);
	const styledCardRef = useRef<HTMLDivElement>(null);
	const handleMouseEnter = useCallback(() => {
		setIsViewed(true);
	}, [setIsViewed]);

	const isFolderHoverCard = isHoverPageCard && type === 'folder';
	const isLivePage = subType === 'live' && status !== 'archived';

	return (
		<StyledCard
			ref={styledCardRef}
			isMenuOpen={isMenuOpen}
			onMouseEnter={handleMouseEnter}
			onFocus={handleMouseEnter}
			isFolderHoverCard={isFolderHoverCard}
			isEndOfPageHoverCard={
				isTriggerCard &&
				expValEquals('confluence_related_pages_gray_hover_cards', 'cohort', 'treatment')
			}
			// prevent clicking on the card from closing it -- folder hover cards are not interactive
			onClick={(e) => isFolderHoverCard && e.stopPropagation()}
			data-testid="page-card-card-container"
			role="listitem"
		>
			<LivePagePreloader isLivePage={isLivePage}>{children}</LivePagePreloader>
		</StyledCard>
	);
};
